import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { FooterBar } from './components/FooterBar';
import { GeoBlockDialog } from './components/GeoBlock';
import { PushPrompt, usePushSync } from './components/PushPrompt';
import { StaffSessionBar } from './components/StaffSessionBar';
import { useGeoBlock } from './lib/geo';
import { lastSymbol, MobileTabBar, MobileTopBar } from './components/mobile/chrome';
import { MobileMenu } from './components/mobile/MobileMenu';
import { cx, Spinner } from './components/ui';
import { TickerStrip } from './components/TickerStrip';
import { MarketSearch } from './components/MarketSearch';
import { TopNav } from './components/TopNav';
import { DemoBar } from './components/DemoAccess';
import { useIsDesktop } from './lib/media';
import { match, navigate, usePath } from './lib/router';
import { AccountLayout } from './pages/account/AccountLayout';
import { AdminPage } from './pages/AdminPage';
import { RequireAuth } from './components/RequireAuth';
import { LoginPage } from './pages/Login';
import { RegisterPage } from './pages/Register';
import { AccountStatusBar } from './pages/onboarding/AccountLock';
import { LEGAL_PATHS } from './pages/legal/paths';
import { isLearnPath } from './pages/academy/learn/routes';
import { MarketsPage } from './pages/MarketsPage';
import { AlertBuilder } from './components/AlertBuilder';
import { AlertToasts } from './components/AlertToasts';
import { PortfolioPage } from './pages/PortfolioPage';
import { EarnPage } from './pages/earn/EarnPage';
import { TradePage } from './pages/TradePage';
import { WelcomePage } from './pages/Welcome';
import { loadMarket } from './stores/market';
import { bootstrapSession, useMe, useSession } from './stores/session';
import { useAccountEvents } from './stores/account';
import { useNotificationsSync } from './stores/notifications';
import { useActionLinksSync } from './stores/actionLinks';
import { useDocumentsSync } from './stores/documents';
import { ActionLinkBar } from './components/ActionLinkBar';
import { ActionLinkPage } from './pages/ActionLinkPage';
import { ws } from './lib/ws';
import type { PresencePage } from '@platform/shared';
import { useChartSync } from './stores/chart';
import { useInstantSync } from './stores/instant';
import { useMemeSync } from './stores/meme';
import { useTradingSync } from './stores/trading';
import { useInvestmentsSync } from './stores/investments';
import { useManagedSync } from './stores/managed';

// Rarely visited pages load on demand to keep the trading bundle small.
const OnboardingPage = lazy(() => import('./pages/onboarding/Onboarding').then((m) => ({ default: m.OnboardingPage })));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword').then((m) => ({ default: m.ResetPasswordPage })));
// "Log in as client" from the CRM.
const CrmLoginPage = lazy(() => import('./pages/CrmLogin'));
// "Open Platform" from the CRM: a staff member's own login.
const StaffLoginPage = lazy(() => import('./pages/StaffLogin'));
const LegalRoute = lazy(() => import('./pages/legal/LegalPage'));
// Loaded on demand so the Academy's lessons don't grow the main (trading) bundle.
const AcademyPage = lazy(() => import('./pages/academy/AcademyPage').then((m) => ({ default: m.AcademyPage })));
// The public Learn hub (articles and courses); its articles load one by one.
const LearnPage = lazy(() => import('./pages/academy/learn/LearnPage'));
// Loaded on demand so Crypto Pro doesn't grow the main (trading) bundle.
const ProView = lazy(() => import('./pages/pro/ProView'));
const MemePage = lazy(() => import('./pages/meme/MemePage'));
const CopyPage = lazy(() => import('./pages/copy/CopyPage'));
const PortfoliosPage = lazy(() => import('./pages/portfolios/PortfoliosPage'));
// Loaded on demand so the asset page doesn't grow the main (trading) bundle.
const AssetPage = lazy(() => import('./pages/markets/asset/AssetPage'));
const CalendarView = lazy(() => import('./pages/calendar/CalendarView'));
const GuideView = lazy(() => import('./pages/calendar/GuideView'));

function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}

/** Home: the trade dashboard (desktop) or Portfolio (phone) when signed in, sign-in otherwise. */
function Landing({ desktop }: { desktop: boolean }) {
  const s = useSession();
  useEffect(() => {
    if (s.ready) navigate(s.me ? (desktop ? '/trade/BTC' : '/portfolio') : desktop ? '/login' : '/welcome', { replace: true });
  }, [s.ready, s.me, desktop]);
  return (
    <div className="grid flex-1 place-items-center text-muted">
      <Spinner />
    </div>
  );
}

function Routes({ desktop }: { desktop: boolean }) {
  const path = usePath();
  let m: Record<string, string> | null;
  if (path === '/') return <Landing desktop={desktop} />;
  if (path === '/trade') return <Redirect to={`/trade/${desktop ? 'BTC' : lastSymbol()}`} />;
  // Market data and trading are for signed-in users only.
  if ((m = match('/trade/:symbol', path)))
    return (
      <RequireAuth>
        <TradePage symbol={m.symbol!.toUpperCase()} />
      </RequireAuth>
    );
  if (path === '/markets')
    return (
      <RequireAuth>
        <MarketsPage />
      </RequireAuth>
    );
  // The economic calendar needs an account; its event guides are public, like the Academy.
  if (path === '/markets/calendar')
    return (
      <RequireAuth>
        <Suspense fallback={null}>
          <CalendarView />
        </Suspense>
      </RequireAuth>
    );
  if ((m = match('/markets/calendar/:slug', path)))
    return (
      <Suspense fallback={null}>
        <GuideView slug={m.slug!} />
      </Suspense>
    );
  if ((m = match('/markets/:symbol', path)))
    return (
      <RequireAuth>
        <Suspense fallback={null}>
          <AssetPage symbol={m.symbol!.toUpperCase()} />
        </Suspense>
      </RequireAuth>
    );
  if ((LEGAL_PATHS as readonly string[]).includes(path)) {
    return (
      <Suspense fallback={null}>
        <LegalRoute path={path} />
      </Suspense>
    );
  }
  // The Wallet is a section of the account area, at its own top-level URL.
  if (path === '/account' || path.startsWith('/account/') || path === '/wallet')
    return (
      <RequireAuth>
        <AccountLayout />
      </RequireAuth>
    );
  if (path === '/earn' || path.startsWith('/earn/'))
    return (
      <RequireAuth>
        <EarnPage />
      </RequireAuth>
    );
  if (path === '/meme' || path.startsWith('/meme/'))
    return (
      <RequireAuth>
        <Suspense fallback={null}>
          <MemePage />
        </Suspense>
      </RequireAuth>
    );
  if (path === '/pro')
    return (
      <RequireAuth>
        <Suspense fallback={null}>
          <ProView />
        </Suspense>
      </RequireAuth>
    );
  if (path === '/copy' || path.startsWith('/copy/'))
    return (
      <RequireAuth>
        <Suspense fallback={null}>
          <CopyPage />
        </Suspense>
      </RequireAuth>
    );
  if (path === '/portfolios' || path.startsWith('/portfolios/') || path === '/portfolio/smart' || path.startsWith('/portfolio/smart/'))
    return (
      <RequireAuth>
        <Suspense fallback={null}>
          <PortfoliosPage />
        </Suspense>
      </RequireAuth>
    );
  // The Learn hub is public (search engines, visitors before sign-up); the interactive tracks need an account.
  if (isLearnPath(path))
    return (
      <Suspense fallback={null}>
        <LearnPage />
      </Suspense>
    );
  if (path === '/academy' || path.startsWith('/academy/'))
    return (
      <RequireAuth>
        <Suspense fallback={null}>
          <AcademyPage />
        </Suspense>
      </RequireAuth>
    );
  // A secure link from the client's account manager (ACTION_LINKS_PLAN.md).
  if ((m = match('/l/:token', path)))
    return (
      <RequireAuth>
        <ActionLinkPage token={m.token!} />
      </RequireAuth>
    );
  // Referrals have their own page in Earn.
  if (path === '/referrals') return <Redirect to="/earn/referrals" />;
  if (path === '/admin')
    return (
      <RequireAuth admin>
        <AdminPage />
      </RequireAuth>
    );
  if (path === '/portfolio')
    return (
      <RequireAuth>
        <PortfolioPage />
      </RequireAuth>
    );
  return (
    <div className="grid flex-1 place-items-center text-muted">
      <p>Page not found</p>
    </div>
  );
}

/** Auth pages are full-screen (their own split layout), outside the app chrome. */
const AUTH_PAGES: Record<string, ComponentType> = {
  '/welcome': WelcomePage,
  '/login': LoginPage,
  '/register': RegisterPage,
  '/forgot-password': ForgotPasswordPage,
  '/reset-password': ResetPasswordPage,
  '/crm-login': CrmLoginPage,
  '/staff-login': StaffLoginPage,
};

/** Presence page for a route (what the back office sees as "where the client is"). */
function presencePage(path: string): PresencePage {
  if (path.startsWith('/trade') || path === '/markets' || path.startsWith('/markets/') || path.startsWith('/meme')) return 'trade';
  if (path === '/wallet') return 'wallet';
  if (path === '/portfolio' || path.startsWith('/portfolio/')) return 'portfolio';
  if (path === '/account/deposit') return 'deposit';
  if (path === '/account/withdraw') return 'withdraw';
  if (path === '/account/kyc') return 'kyc';
  if (path === '/account/support') return 'support';
  if (path === '/account' || path.startsWith('/account/')) return 'account';
  return 'other';
}

export function App() {
  const geo = useGeoBlock(useMe());
  const app = <AppShell />;
  if (!geo) return app;
  // Restricted country: the app stays visible behind the popup but can't be used.
  return (
    <>
      <div inert className="contents">
        {app}
      </div>
      <GeoBlockDialog {...geo} />
    </>
  );
}

function AppShell() {
  const path = usePath();
  const desktop = useIsDesktop();
  useEffect(() => ws.setPresence(presencePage(path)), [path]);
  useNotificationsSync();
  useActionLinksSync();
  useDocumentsSync();
  usePushSync();
  useEffect(() => {
    void loadMarket();
    // /crm-login and /staff-login start their own session from the link.
    if (window.location.pathname !== '/crm-login' && window.location.pathname !== '/staff-login') void bootstrapSession();
  }, []);
  // A new sign-up who hasn't sent details and documents yet finishes that first (legal pages stay readable).
  const me = useMe();
  const step = me?.onboardingStep;
  // A dealer login only works the dealer desk: every trading-platform page sends them to /admin
  // (sign-in pages and legal pages stay reachable).
  const dealerAway = me?.role === 'dealer' && path !== '/admin' && !AUTH_PAGES[path] && !(LEGAL_PATHS as readonly string[]).includes(path);
  useEffect(() => {
    if (dealerAway) navigate('/admin', { replace: true });
    else if (me?.role !== 'dealer' && (step === 'details' || step === 'identity') && !path.startsWith('/onboarding') && !(LEGAL_PATHS as readonly string[]).includes(path)) navigate('/onboarding', { replace: true });
  }, [dealerAway, me?.role, step, path]);
  useTradingSync();
  useManagedSync();
  useInvestmentsSync();
  useInstantSync();
  useChartSync();
  useAccountEvents();
  useMemeSync();
  const AuthPage = path === '/onboarding' || path.startsWith('/onboarding/') ? OnboardingPage : AUTH_PAGES[path];
  const overlays = (
    <>
      <AlertBuilder />
      <AlertToasts />
      <MarketSearch />
    </>
  );
  if (dealerAway) return null;
  if (AuthPage) {
    return (
      <Suspense fallback={null}>
        <AuthPage />
      </Suspense>
    );
  }
  // Admin has its own full-screen layout (sidebar menu, no trading chrome).
  if (path === '/admin') {
    return (
      <div className="flex min-h-full flex-col lg:h-full">
        <StaffSessionBar />
        <main className="flex min-h-0 flex-1 flex-col">
          <RequireAuth admin>
            <AdminPage />
          </RequireAuth>
        </main>
      </div>
    );
  }
  if (desktop) {
    return (
      <div className="flex min-h-full flex-col lg:h-full">
        <StaffSessionBar />
        <TopNav />
        <DemoBar />
        <AccountStatusBar />
        <ActionLinkBar />
        <TickerStrip />
        <main className="flex min-h-0 flex-1 flex-col">
          <Routes desktop />
        </main>
        <FooterBar />
        {overlays}
        <PushPrompt />
      </div>
    );
  }
  // Phones and small tablets: each page scrolls with the document, a floating tab bar, and the full-screen menu.
  const trade = path === '/' || path === '/trade' || path.startsWith('/trade/');
  // An asset page (/markets/BTC) has its own top bar and pins its Buy / Sell bar like the trade screen.
  const asset = path.startsWith('/markets/') && !path.startsWith('/markets/calendar');
  const ownTopBar = trade || asset || path === '/portfolio' || path === '/markets';
  // A meme token page pins its own Buy / Sell bar where the tab bar would float.
  const pinnedBar = trade || asset || /^\/meme\/[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(path);
  return (
    <div className="flex min-h-full flex-col bg-bg">
      <StaffSessionBar />
      {!ownTopBar && <MobileTopBar />}
      <AccountStatusBar />
      <ActionLinkBar />
      <main className={cx('flex min-h-0 flex-1 flex-col', !pinnedBar && 'pb-[calc(env(safe-area-inset-bottom)+112px)]')}>
        <Routes desktop={false} />
      </main>
      {!pinnedBar && <MobileTabBar />}
      <MobileMenu />
      {overlays}
      <PushPrompt />
    </div>
  );
}
